import React from 'react'
import ButtonsListPopover from '@/scenes/Editor/components/FloatingInspectorButton/SharedComponents/Popover/ButtonsListPopover'
import ImagesGridPopover from '@/scenes/Editor/components/FloatingInspectorButton/SharedComponents/Popover/ImagesGridPopover'
import StarDefaultIcon from '@/components/icons/StarDefaultIcon'
import type { SuggestionButtonPopover } from '@/scenes/Editor/components/EditWithAI/components/suggestedButtonSets'

type PopoverPlacement = 'top' | 'bottom' | 'left' | 'right' | 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight'

export interface SuggestionPopoverOptions {
  /** Short menus only: no title or close button, like a native menu by its row. */
  bare?: boolean
  /** Gap to the anchor (px). */
  margin?: number
}

/**
 * Turns a `SuggestionButtonPopover` config into the popover component its
 * `designType` names. Lives here rather than inside `EditWithAISuggestionsBar`
 * so any trigger — a suggestion pill, an inspector row — can render one of the
 * shared configs in `src/components/AIPopovers/`.
 *
 * `onSelectItem` is the fallback for items that carry no `onClick` of their own;
 * configs built with explicit per-item handlers can leave it out. `placement`
 * defaults to `'top'` (the suggestions bar's anchor-above convention) — the
 * Batch AI tools panels pass `'bottomLeft'` since their triggers sit inside a
 * scrolling list rather than a floating bar.
 */
export function renderSuggestionPopover(
  popover: SuggestionButtonPopover,
  buttonRef: React.RefObject<HTMLElement>,
  closePopover: (() => void) | undefined,
  onSelectItem: (value: string) => void = () => {},
  placement: PopoverPlacement = 'top',
  options: SuggestionPopoverOptions = {},
): React.ReactNode {
  const { bare = false, margin } = options
  if (popover.designType === 'list') {
    return (
      <ButtonsListPopover
        buttonRef={buttonRef}
        objects={popover.items.map((item) => ({
          icon: item.icon || <StarDefaultIcon size={20} />,
          txt: item.text,
          value: item.value,
          onClick: item.onClick,
          closePopoverOnClick: item.closePopoverOnClick ?? true,
        }))}
        onSelectItem={(value) => onSelectItem(value)}
        width={popover.width ?? 320}
        height={popover.height}
        maxHeight={popover.maxHeight}
        placement={placement}
        title={popover.title}
        showCloseButton
        onClose={() => { closePopover?.() }}
        variant="suggest"
        categories={popover.categories}
      />
    )
  }

  if (popover.designType === 'list_white') {
    return (
      <ButtonsListPopover
        buttonRef={buttonRef}
        objects={popover.items}
        onSelectItem={(value) => onSelectItem(value)}
        width={popover.width ?? 'auto'}
        minWidth={popover.minWidth}
        maxWidth={popover.maxWidth}
        height={popover.height}
        maxHeight={popover.maxHeight}
        placement={placement}
        title={bare ? undefined : popover.title}
        showCloseButton={!bare}
        onClose={() => { closePopover?.() }}
        variant="menu"
        categories={popover.categories}
        margin={margin}
        padding={bare ? '8,16' : undefined}
      />
    )
  }

  return (
    <ImagesGridPopover
      buttonRef={buttonRef}
      objects={popover.items}
      onSelectItem={(value) => onSelectItem(value)}
      columns={popover.columns}
      aspectRatio={popover.aspectRatio}
      skeletonCount={popover.skeletonCount}
      scrollToBottom={popover.scrollToBottom}
      width={popover.width ?? 320}
      height={popover.height}
      maxHeight={popover.maxHeight}
      placement={placement}
      title={popover.title}
      showCloseButton
      onClose={() => { closePopover?.() }}
      padding={'12,16,16,16'}
      gap={8}
      categories={popover.categories}
      onLoadMore={popover.onLoadMore}
      isLoadingMore={popover.isLoadingMore}
      loadMoreSkeletonCount={popover.loadMoreSkeletonCount}
      skeletonVariant={popover.skeletonVariant}
    />
  )
}

export default renderSuggestionPopover
